import type {
  State,
  SummaryState,
  TimelineSectionType,
} from '@motional-cc/fe/interface/api/api-concierge';
import type {
  Model as CarModelName,
  Gen2BundleSupportedPlatform,
  Gen2Platform,
  ModernModel as ModernCarModelName,
  OneTouchStepStatus,
  Region,
  TestPlatform,
} from '@motional-cc/fe/interface/api/registrar';
import type { FRResolutionType } from '@motional-cc/fe/interface/api/wuphf';
import {
  CommandState,
  FpdDisplayEventPriority,
  PendingCommandState,
  RejectedCommandState,
} from '@motional-cc/fe/interface/api/api-cerebro';
import { ActiveTaskEventState } from '@motional-cc/fe/interface/api/api-concierge';
import { RealPartnerName } from '@motional-cc/fe/interface/api/api-server';
import { VicMode } from '@motional-cc/fe/interface/api/armada';
import {
  EventType,
  VehicleCommandName,
} from '@motional-cc/fe/interface/api/kamaji';
import {
  BenignAlertLevel,
  CarMode,
  GoalType,
} from 'src/interface/command-center/unsorted-types';
import assertArrayExhaustsType from 'src/tools/assertArrayExhaustsType';

export const VEHICLE_STATES = assertArrayExhaustsType<State>()([
  'Unavailable',
  'Available',
  'WaitingForOnboardResponse',
  'WaitingForDispatchProceed',
  'EnrouteToGoal',
  'ArrivedAtGoal',
]);

export const VEHICLE_SUMMARY_STATES = assertArrayExhaustsType<SummaryState>()([
  'ON_RIDE',
  'ON_DISPATCH',
  'ON_MISSION',
  'ONLINE',
  'PARTIALLY_ONLINE',
  'DEPLOYMENT_PENDING',
  'DEPLOYMENT_IN_PROGRESS',
  'DEPLOYMENT_FAILED',
  'DEPLOYMENT_MISSING',
  'OFFLINE',
  'REGISTRATION_REVOKED',
  'OUT_OF_SERVICE',
  'UNKNOWN',
]);

export const CAR_MODES = assertArrayExhaustsType<CarMode>()([
  'Unknown',
  'Autonomous',
  'Manual',
]);

export const AD_VIC_MODES: VicMode[] = [
  'AD_Drive',
  'AD_Park',
  'AD_Stopping',
  'AD_Degraded_Parked',
];

// Vehicles are ordered as most recent first
export const MODERN_CAR_MODEL_NAMES =
  assertArrayExhaustsType<ModernCarModelName>()(['Ioniq', 'Eval']);
export const CAR_MODEL_NAMES = assertArrayExhaustsType<CarModelName>()([
  'Ioniq',
  'BMW',
  'Pacifica',
  'Eval',
  'Audi',
  'Zoe',
]);

export const REGIONS = assertArrayExhaustsType<Region>()([
  'las_vegas',
  'pittsburgh',
  'singapore',
  'boston',
  'santa_monica',
  'san_diego',
  'austin',
]);

const GEN2_PLATFORMS = assertArrayExhaustsType<Gen2Platform>()([
  'GEN2_AVPilot',
  'GEN2_AVProd',
  'GEN2_AVProdExtLog',
]);

const GEN3_PLATFORMS = assertArrayExhaustsType<TestPlatform>()(['GEN3_Ampere']);

export const GEN2_BUNDLE_SUPPORTED_PLATFORMS =
  assertArrayExhaustsType<Gen2BundleSupportedPlatform>()([
    ...GEN2_PLATFORMS,
    ...GEN3_PLATFORMS,
  ]);

export const TIMELINE_SECTION_TYPES =
  assertArrayExhaustsType<TimelineSectionType>()(['rva', 'ride']);

export const WORK_ORDER_RESOLUTION_TYPES =
  assertArrayExhaustsType<FRResolutionType>()([
    'relaunch',
    'drive_back',
    'tow',
    'false_positive',
  ]);

export const ONE_TOUCH_STEP_STATUSES =
  assertArrayExhaustsType<OneTouchStepStatus>()([
    'pass',
    'fail',
    'error',
    'in_progress',
    'awaiting',
    'unknown',
    'timeout',
    'outdated',
    'approved',
  ]);
// TODO: consider moving this into the swagger
export const FAILED_ONE_TOUCH_STEP_STATUSES: ReadonlyArray<OneTouchStepStatus> =
  ['error', 'fail', 'timeout'];
export const COMPLETED_ONE_TOUCH_STEP_STATUSES: ReadonlyArray<OneTouchStepStatus> =
  ['pass', ...FAILED_ONE_TOUCH_STEP_STATUSES];
export const INACTIVE_ONE_TOUCH_STEP_STATUSES: ReadonlyArray<OneTouchStepStatus> =
  ['awaiting', ...COMPLETED_ONE_TOUCH_STEP_STATUSES];

export const BENIGN_ALERT_LEVELS = assertArrayExhaustsType<BenignAlertLevel>()([
  'success',
  'info',
]);

export const REJECTED_COMMAND_STATES =
  assertArrayExhaustsType<RejectedCommandState>()([
    'Rejected',
    'Expired',
    'Failed',
  ]);

export const PENDING_COMMAND_STATES =
  assertArrayExhaustsType<PendingCommandState>()([
    'Requested',
    'Acknowledged',
    'Running',
    'TimedOut',
  ]);

export const STOP_POLLING_STATES: CommandState[] = [
  ...REJECTED_COMMAND_STATES,
  'Succeeded',
  'TimedOut',
];

export const MESSAGE_PRIORITY_LEVELS =
  assertArrayExhaustsType<FpdDisplayEventPriority>()(['High', 'Medium', 'Low']);

const VEHICLE_COMMAND_NAMES = assertArrayExhaustsType<VehicleCommandName>()([
  'bio break start',
  'bio break end',
  'meal break start',
  'meal break end',
  'meeting start',
  'meeting end',
  'charge start',
  'charge end',
  'deployment start',
  'deployment end',
  'bootup start',
  'bootup end',
  'scenario start',
  'scenario end',
  'mission start',
  'mission end',
  'mission pause',
  'mission resume',
  'troubleshoot start',
  'troubleshoot end',
]);

export const EVENT_TYPES = assertArrayExhaustsType<EventType>()([
  'message',
  'shift start',
  'shift end',
  'unknown',
  ...VEHICLE_COMMAND_NAMES,
]);

export const GOAL_TYPES = assertArrayExhaustsType<GoalType>()([
  'stopless',
  'stop',
  'allPickup',
  'allDropoff',
]);

export const REAL_PARTNER_NAMES = assertArrayExhaustsType<RealPartnerName>()([
  'motional',
  'uber',
  'lyft',
]);

export const ACTIVE_TASK_STATES =
  assertArrayExhaustsType<ActiveTaskEventState>()([
    'Running',
    'Completing',
    'Stopping',
    'Failing',
    'Failed',
    'Skipping',
  ]);
